import {
  PROFILE_ARRAY_FIELDS,
  PROFILE_FIELDS,
  PROFILE_TEXT_FIELDS,
} from '../profileContract/profileFieldVocabulary';

export const SUPPORT_PROFILE_RESOLVER_ARRAY_FIELDS = PROFILE_ARRAY_FIELDS;
export const SUPPORT_PROFILE_RESOLVER_TEXT_FIELDS = PROFILE_TEXT_FIELDS;
export const SUPPORT_PROFILE_RESOLVER_FIELDS = PROFILE_FIELDS;

const ARRAY_FIELDS = new Set(SUPPORT_PROFILE_RESOLVER_ARRAY_FIELDS);
const ALLOWED_FIELDS = new Set(SUPPORT_PROFILE_RESOLVER_FIELDS);

const normalizeText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();
const normalizeValueKey = (value = '') => normalizeText(value).toLowerCase();

const getFactValue = (fact = {}) => normalizeText(fact.summary || fact.title);

const createLegacyFact = ({ field, value, index }) => ({
  id: `legacy-supportProfile:${field}:${index}`,
  childId: '',
  title: normalizeText(value),
  summary: normalizeText(value),
  knowledgeType: 'profile',
  profileField: field,
  factStatus: 'current',
  profileVisibilityOverride: null,
  confidence: 'high',
  needsQuickCheck: false,
  sourceRefs: [],
  resolverEligible: true,
  synthetic: true,
});

export const buildLegacySupportProfileFacts = (supportProfile = {}) => (
  SUPPORT_PROFILE_RESOLVER_FIELDS.flatMap((field) => {
    const rawValue = supportProfile?.[field];
    const values = ARRAY_FIELDS.has(field)
      ? (Array.isArray(rawValue) ? rawValue : [])
      : [rawValue];

    return values
      .map(normalizeText)
      .filter(Boolean)
      .map((value, index) => createLegacyFact({ field, value, index }));
  })
);

const isVisibleKnowledgeFact = (fact = {}) => {
  if (!ALLOWED_FIELDS.has(fact.profileField) || ['needs_review', 'archived'].includes(fact.evidenceReview?.status)) return false;
  if (fact.factStatus !== 'current' || fact.resolverEligible !== true) return false;
  if (fact.profileVisibilityOverride === 'hide') return false;
  if (fact.needsQuickCheck && fact.profileVisibilityOverride !== 'pin') return false;
  return Boolean(getFactValue(fact));
};

const deduplicateFacts = (facts = []) => {
  const deduplicated = [];
  const indexByValue = new Map();

  facts.forEach((fact) => {
    const key = normalizeValueKey(getFactValue(fact));
    if (!key) return;

    const existingIndex = indexByValue.get(key);
    if (existingIndex === undefined) {
      indexByValue.set(key, deduplicated.length);
      deduplicated.push(fact);
      return;
    }

    if (deduplicated[existingIndex].synthetic && !fact.synthetic) {
      deduplicated[existingIndex] = fact;
    }
  });

  return deduplicated;
};

const createEmptyFields = () => Object.fromEntries(
  SUPPORT_PROFILE_RESOLVER_FIELDS.map((field) => [field, ARRAY_FIELDS.has(field) ? [] : ''])
);

const createEmptyFactsByField = () => Object.fromEntries(
  SUPPORT_PROFILE_RESOLVER_FIELDS.map((field) => [field, []])
);

export const resolveSupportProfileFacts = ({
  knowledgeFacts = [],
  legacySupportProfile = {},
} = {}) => {
  const factsByField = createEmptyFactsByField();
  const sourceReviewValues=new Set(knowledgeFacts.filter(fact=>['needs_review','archived'].includes(fact.evidenceReview?.status)).map(fact=>`${fact.profileField}:${normalizeValueKey(getFactValue(fact))}`));
  const legacyFacts = buildLegacySupportProfileFacts(legacySupportProfile).filter(fact=>!sourceReviewValues.has(`${fact.profileField}:${normalizeValueKey(getFactValue(fact))}`));
  const visibleKnowledgeFacts = (Array.isArray(knowledgeFacts) ? knowledgeFacts : [])
    .filter(isVisibleKnowledgeFact)
    .map((fact) => ({ ...fact, synthetic: false }));

  [...legacyFacts, ...visibleKnowledgeFacts].forEach((fact) => {
    if (ALLOWED_FIELDS.has(fact.profileField)) {
      factsByField[fact.profileField].push(fact);
    }
  });

  const fields = createEmptyFields();
  SUPPORT_PROFILE_RESOLVER_FIELDS.forEach((field) => {
    factsByField[field] = deduplicateFacts(factsByField[field]);
    const values = factsByField[field].map(getFactValue);
    fields[field] = ARRAY_FIELDS.has(field) ? values : values.join('\n\n');
  });

  return { fields, factsByField };
};

export const buildDisplaySupportProfile = ({
  knowledgeFacts = [],
  legacySupportProfile = {},
} = {}) => ({
  ...(legacySupportProfile || {}),
  ...resolveSupportProfileFacts({ knowledgeFacts, legacySupportProfile }).fields,
});
